import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { SearchableDropdown } from './SearchableDropdown';
import { DropdownOption } from '../Dropdown';
import { toTitleCase } from '../../lib/strings';
import { anyToken } from '../../constants';
import { AccountLineageGraphResponse } from '../../types/tripGroup';

export const anyString = 'Any';
const emptyStringDisplayVal = '[Empty Value]';

const OuterBox = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
`;

const InputRowWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  margin: 4px;
`;

const FixedWidthLabel = styled.label`
  display: inline-block;
  min-width: 110px;
  max-width: 110px;
  font-weight: 400;
`;

const FixedWidthButton = styled.button`
  width: 230px;
  margin-left: 110px;
`;

type FlatFilterMap = Record<string, string[]>;

type FilterOptionMap = Record<string, DropdownOption<string>[]>;

type FlatStringObject = Record<string, string>;

interface HierarchyNavigatorProps {
  hierarchy: AccountLineageGraphResponse;
  filterValues: FlatStringObject;
  setFilterValues: (values: FlatStringObject) => void;
  isDisabled?: boolean;
  showClear?: boolean;
  doReset?: boolean;
  setDoReset?: (value: boolean) => void;
}

const isNullishOrAny = (value: any): boolean => {
  return value == null || value.toString() === anyToken;
};

// the intent of this is to get a flat list of values from a hierarchy
//  based on a set of incoming filters
const getFlatFilteredHierarchyValues = (
  tier: any,
  tierNames: any[],
  filterValues: FlatStringObject = {},
  depth: number = 1,
  flat: FlatFilterMap = {},
): FlatFilterMap => {
  if (tier) {
    const tierName = tierNames[depth].name;

    tier.map((tierEntry: { value: string; children: string | any[]; }) => {
      const conformsToValueFilter =
        isNullishOrAny(filterValues[tierName]) ||
        tierEntry.value.toString().toUpperCase() ===
          filterValues[tierName].toString().toUpperCase();

      if (
        conformsToValueFilter &&
        !flat[tierName]?.includes(tierEntry.value)
      ) {
        if (!flat[tierName]) {
          flat[tierName] = [];
        }
        flat[tierName].push(tierEntry.value);

        const nextDepth = depth + 1;
        if (tierEntry.children?.length) {
          getFlatFilteredHierarchyValues(
            tierEntry.children,
            tierNames,
            filterValues,
            nextDepth,
            flat,
          );
        } else if (tierNames[nextDepth] && !flat[tierNames[nextDepth].name]) {
          flat[tierNames[nextDepth].name] = [];
        }
      }
    });

    // any passed-in filter values will be constrained beyond what we want them to be for the dropdowns,
    //   so after they're used for the recursive check exclude them from the result, w/ the
    //   expected usecase being to Object.assign the result over the existing dropdown settings
    Object.keys(filterValues).forEach(filterValue => {
      !isNullishOrAny(filterValues[filterValue]) && delete flat[filterValue];
    });
  }
  return flat;
};

// this converts a flat map of option strings to dropdownOptions type items
const getFilterOptionMap = (
  flatFilters: FlatFilterMap,
  includeAny: boolean = true,
): FilterOptionMap => {
  const initialValue = includeAny ? [{ value: anyToken, text: anyString }] : [];
  return Object.entries(flatFilters).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = [
        ...initialValue,
        ...value
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
          .map(optionName => {
            return {
              value: optionName,
              text: optionName || emptyStringDisplayVal,
            };
          }),
      ];
    }
    return acc;
  }, {} as FilterOptionMap);
};

interface GetFiltersToUpdateResult {
  filters: FlatStringObject;
  values: FlatFilterMap;
}

const getFiltersToUpdate = (
  newFilterValues: FlatStringObject,
  hierarchy: AccountLineageGraphResponse,
): GetFiltersToUpdateResult => {
  return hierarchy.accountAttributes.reduce(
    (acc, attr) => {
      const attribute = attr.name;
      const currentFilterOptions = acc.values[attribute];
      const currentFilterValue = newFilterValues[attribute];
      // ignore empty, as well as any whose values are present, but not in the previous rounds' results
      if (
        currentFilterValue != null &&
        (currentFilterValue === anyToken ||
          !currentFilterOptions ||
          currentFilterOptions.includes(currentFilterValue))
      ) {
        acc.filters = {
          ...acc.filters,
          [attribute]: currentFilterValue,
        };

        const filtersToUpdate = getFlatFilteredHierarchyValues(
          hierarchy.root.children,
          hierarchy.accountAttributes,
          acc.filters,
        );

        acc.values = {
          ...acc.values,
          ...filtersToUpdate,
        };
      }
      return acc;
    },
    { filters: {}, values: {} } as GetFiltersToUpdateResult,
  );
};

export const HierarchyNavigator: React.FC<HierarchyNavigatorProps> = ({
  hierarchy,
  filterValues,
  setFilterValues,
  isDisabled = false,
  showClear = false,
  doReset = false,
  setDoReset,
}) => {
  // this will get a flat list of all possible values at each layer, to be converted to an array name+option set objects
  // also useful for implementing a reset button
  const flatFilters: FlatFilterMap = getFlatFilteredHierarchyValues(
    hierarchy.root.children,
    hierarchy.accountAttributes,
  );
  const initialFilters = getFilterOptionMap(flatFilters);
  // this will then init to an array of objects each with a
  const [filterOptions, setFilterOptions] = useState(initialFilters);

  // init a map of states of all dropdowns, setting each to "Any"
  const initialFilterValues = Object.fromEntries(
    hierarchy.accountAttributes.map(key => [key.name, anyToken]),
  );

  useEffect(() => {
    if (Object.keys(flatFilters).length || doReset) {
      updateFilterOptionsAndValues(filterValues);
      setDoReset?.(false);
    }
  }, [Object.keys(flatFilters).length, doReset]);

  const clear = () => {
    setFilterOptions(initialFilters);
    setFilterValues(initialFilterValues);
  };

  const updateFilterOptionsAndValues = (localFilterValues: FlatStringObject) => {
    const filtersValuesToUpdate = getFiltersToUpdate(
      localFilterValues,
      hierarchy,
    );

    const newFilters = { ...filterValues, ...filtersValuesToUpdate.filters };
    setFilterOptions(currentFilterOptions => {
      const newFilterOptionMap = {
        ...currentFilterOptions,
        ...getFilterOptionMap(filtersValuesToUpdate.values),
      };

      // this is so states that are not in newly filtered options don't remain in filterValues state
      hierarchy.accountAttributes.forEach(attr => {
        const attribute = attr.name;
        if (
          attribute !== 'ACCOUNT_ID' &&
          !newFilterOptionMap[attribute]?.some(
            option => option.value === newFilters[attribute],
          )
        ) {
          newFilters[attribute] = anyToken;
        }
      });
      // defer this b/c otherwise it causes react race conditions
      setTimeout(() => {
        setFilterValues(newFilters);
      }, 0);
      return newFilterOptionMap;
    });
  };

  const dropdownChangeHandler = (name: string, value: string) => {
    // set the values of the various filter dropdowns
    const newFilterValues = {
      ...filterValues,
      [name]: value,
    };

    updateFilterOptionsAndValues(newFilterValues);
  };

  return (
    <>
      <fieldset disabled={isDisabled}>
        <OuterBox>
          {Object.entries(filterOptions).map(([name, options]) => {
            if (Array.isArray(options)) {
              // Object.entries forces the value portion to mixed type regardless of
              //  whether the original object typed it clearly, so you have to cast it through 'any'
              const typeCoercedOptions: DropdownOption<string>[] = options as any;
              return (
                <InputRowWrapper key={`${name}Filter`}>
                  <FixedWidthLabel htmlFor={name}>
                    {`${toTitleCase(name)}:`}
                  </FixedWidthLabel>
                  <SearchableDropdown
                    id={name}
                    name={name}
                    options={typeCoercedOptions}
                    value={filterValues[name]}
                    onChange={optionValue => {
                      dropdownChangeHandler(name, String(optionValue) ?? anyToken);
                    }}
                  />
                </InputRowWrapper>
              );
            }
          })}
          {showClear && (
            <FixedWidthButton onClick={clear}>CLEAR</FixedWidthButton>
          )}
        </OuterBox>
      </fieldset>
    </>
  );
};
